import { useState, useEffect } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import NetworkBackground from './NetworkBackground';

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="home" className="relative min-h-screen bg-[#060E1A] overflow-hidden flex flex-col justify-center">
      {/* Animated network canvas */}
      <NetworkBackground opacity={0.5} />

      {/* Radial glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-0 w-[800px] h-[800px] bg-[#E8500A]/7 rounded-full blur-[160px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#1a3a6e]/50 rounded-full blur-[120px]" />
      </div>

      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8500A]/50 to-transparent" />

      {/* Main content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full pt-32 pb-28 lg:py-0 lg:min-h-screen flex flex-col lg:justify-center">
        <div className="max-w-4xl xl:max-w-5xl">
          {/* Eyebrow */}
          <div
            className="flex items-center gap-3 mb-10"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.7s ease 0.1s' }}
          >
            <span className="w-8 h-px bg-[#E8500A]" />
            <span
              className="text-[#E8500A] text-xs font-semibold uppercase tracking-[0.3em]"
              style={{ fontFamily: '"Inter", sans-serif' }}
            >
              Dallas, Texas · Recruitment & Talent Solutions
            </span>
          </div>

          {/* Headline */}
          <h1
            className="text-6xl sm:text-7xl lg:text-[5.5rem] xl:text-[6.5rem] font-black text-white leading-[0.92] tracking-tight mb-8"
            style={{
              fontFamily: '"Satoshi", "Inter", sans-serif',
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.9s ease 0.2s',
            }}
            data-testid="hero-headline"
          >
            Build High-<br />
            <span className="text-[#E8500A]">Performance</span><br />
            Teams, Faster.
          </h1>

          {/* Sub-copy */}
          <p
            className="text-lg lg:text-xl text-white/55 font-light leading-relaxed mb-12 max-w-2xl"
            style={{
              fontFamily: '"Inter", sans-serif',
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(16px)',
              transition: 'all 0.9s ease 0.35s',
            }}
            data-testid="hero-subtext"
          >
            Execution-led recruiting for product tech, fintech, and life sciences companies across the U.S.{' '}
            <span className="text-white/35">We deliver candidates with clarity, context, and conviction.</span>
          </p>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-4 mb-20"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(14px)',
              transition: 'all 0.9s ease 0.5s',
            }}
          >
            <button
              onClick={() => scrollTo('contact')}
              className="group flex items-center justify-center gap-2.5 bg-[#E8500A] text-white px-10 py-4.5 text-sm font-bold uppercase tracking-wider hover:bg-[#d44309] hover:-translate-y-0.5 transition-all duration-200 rounded-sm"
              style={{ fontFamily: '"Inter", sans-serif', boxShadow: '0 0 0 0 rgba(232,80,10,0)', paddingTop: '1.1rem', paddingBottom: '1.1rem' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 12px 32px rgba(232,80,10,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 0 0 rgba(232,80,10,0)'; }}
              data-testid="hero-book-call-btn"
            >
              Book a Call
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <button
              onClick={() => scrollTo('case-studies')}
              className="flex items-center justify-center gap-2 border border-white/22 text-white/80 px-10 py-4.5 text-sm font-bold uppercase tracking-wider hover:border-white/50 hover:text-white hover:bg-white/5 transition-all duration-200 rounded-sm"
              style={{ fontFamily: '"Inter", sans-serif', paddingTop: '1.1rem', paddingBottom: '1.1rem' }}
              data-testid="hero-view-work-btn"
            >
              View Our Work
            </button>
          </div>

          {/* Stat pills */}
          <div
            className="flex flex-wrap items-center gap-x-10 gap-y-4 border-t border-white/8 pt-8"
            style={{ opacity: loaded ? 1 : 0, transition: 'opacity 1s ease 0.7s' }}
          >
            {[
              { n: '120+', l: 'Offers Made' },
              { n: '15+', l: 'Client Partners' },
              { n: '30+', l: 'Yrs Combined Experience' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-4">
                {i > 0 && <div className="hidden sm:block w-px h-7 bg-white/10" />}
                <div>
                  <div className="text-xl font-black text-white leading-none" style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}>{s.n}</div>
                  <div className="text-xs text-white/35 mt-1" style={{ fontFamily: '"Inter",sans-serif' }}>{s.l}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <button
        onClick={() => scrollTo('credibility')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 opacity-30 hover:opacity-60 transition-opacity duration-300"
        aria-label="Scroll down"
      >
        <span className="text-white text-[10px] uppercase tracking-[0.2em]" style={{ fontFamily: '"Inter",sans-serif' }}>Scroll</span>
        <ChevronDown size={18} className="text-white animate-bounce" />
      </button>
    </section>
  );
}
